// React
import { useEffect, useState } from 'react'

// React Router
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

// Hook
import { useDebouncedValue, useDidUpdate } from '@mantine/hooks'

// Axios API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// useTable hook
export default function useKanban({
    availableColumns = [],
    availableFilters,
    engineApiEndpoint,
}) {
    // Axios API
    const { requestGet, loading } = useAxiosApi()

    // Initialize router
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // Table state
    const [kanbanState, setKanbanState] = useState({
        filters: [],
        query: null,
        columns: [],
        location_id: null,
    })

    // Query
    const [query, setQuery] = useState('')
    const [debouncedQuery] = useDebouncedValue(query, 500)

    useDidUpdate(() => {
        updateURLWithNewState({
            ...kanbanState,
            query,
        })
    }, [debouncedQuery])

    // Filters
    const changeFilter = (key, value) => {
        const newFilters = {
            ...kanbanState.filters,
            [key]: value,
        }

        updateURLWithNewState({
            ...kanbanState,
            filters: newFilters,
        })
    }

    const changeFilters = (newFilters) => {
        updateURLWithNewState({
            ...kanbanState,
            filters: newFilters,
        })
    }

    const resetFilters = () => {
        updateURLWithNewState({
            ...kanbanState,
            filters: {},
        })
    }

    // Location id
    const changeLocationId = (location_id) => {
        updateURLWithNewState({
            ...kanbanState,
            location_id,
        })
    }

    // Columns
    const columns = availableColumns.filter((column) => {
        // If there is no columns in tableState, use default columns
        if (!kanbanState.columns.length) {
            return (
                column.display === 'optional_default' ||
                column.display === 'mandatory'
            )
        }

        return (
            kanbanState.columns.includes(column.key) ||
            column.display === 'mandatory'
        )
    })

    // Fetching data
    const [kanbanData, setKanbanData] = useState(null)
    const [isFetching, setIsFetching] = useState(false)
    const [isFirstFetch, setIsFirstFetch] = useState(true)

    const fetchData = async (tableState, shouldScrollTop = true) => {
        // Fetch data from Engine API
        if (engineApiEndpoint) {
            // Set isFetching to true
            setIsFetching(true)

            const params = {
                filters: tableState.filters,
                search: tableState.query,
                location_id: tableState.location_id,
            }

            // Make parallel requests for each column (getting data for each column)
            const requests = []

            for (const column of tableState.columns) {
                requests.push(
                    requestGet(engineApiEndpoint, {
                        ...params,
                        column,
                    })
                )
            }

            const results = await Promise.all(requests)

            const resultsData = results.map((result) => result.data)

            // Order columns by order in availableColumns
            const orderedResultsData = resultsData.sort((a, b) => {
                const aIndex = availableColumns.findIndex(
                    (column) => column.key === a.column
                )

                const bIndex = availableColumns.findIndex(
                    (column) => column.key === b.column
                )

                return aIndex - bIndex
            })

            setKanbanData(orderedResultsData)

            // Set isFetching to false
            setIsFetching(false)
        }
    }

    const refetchData = async (shouldScrollTop = true) => {
        fetchData(kanbanState, shouldScrollTop)
    }

    // Fetch more items for a specific column (infinite scroll)
    const [fetchingMoreColumns, setFetchingMoreColumns] = useState({})

    const fetchMore = async (columnSlug) => {
        if (!engineApiEndpoint || !kanbanData) return
        if (fetchingMoreColumns[columnSlug]) return

        const column = kanbanData.find((col) => col.slug === columnSlug)
        if (!column || column.items.length >= column.total) return

        setFetchingMoreColumns((prev) => ({ ...prev, [columnSlug]: true }))

        const params = {
            filters: kanbanState.filters,
            search: kanbanState.query,
            location_id: kanbanState.location_id,
            column: columnSlug,
            offset: column.items.length,
        }

        const result = await requestGet(engineApiEndpoint, params)

        setKanbanData((prev) =>
            prev.map((col) =>
                col.slug === columnSlug
                    ? { ...col, items: [...col.items, ...result.data.items] }
                    : col
            )
        )

        setFetchingMoreColumns((prev) => ({ ...prev, [columnSlug]: false }))
    }

    const updateURLWithNewState = (newState) => {
        // Update URL
        const query = []

        // Set filters for URL (comma separated if array)
        for (const key in newState.filters) {
            const filterProps = availableFilters.find(
                (filter) => filter.key === key
            )

            // If filter is available
            if (filterProps && filterProps.type) {
                // If filter is of type select or multiselect
                if (filterProps.type === 'multiselect') {
                    if (newState.filters[key].length > 0) {
                        query.push({
                            key,
                            value: newState.filters[key].join(','),
                        })
                    }
                } else if (filterProps.type === 'select') {
                    if (newState.filters[key]) {
                        query.push({
                            key,
                            value: newState.filters[key],
                        })
                    }
                } else if (filterProps.type === 'boolean') {
                    if (newState.filters[key] != null && newState.filters[key] !== '') {
                        query.push({
                            key,
                            value: newState.filters[key],
                        })
                    }
                }
            }
        }

        // Set search query
        if (newState.query && newState.query.trim()) {
            query.push({
                key: 'query',
                value: newState.query.trim(),
            })
        }

        // Set order by direction

        // Set columns
        const defaultColumns = availableColumns.filter(
            (column) => column.display === 'optional_default'
        )

        // Set location_id
        if (newState.location_id) {
            query.push({
                key: 'location_id',
                value: newState.location_id,
            })
        }

        const isDefaultColumns =
            JSON.stringify(newState.columns) ===
            JSON.stringify(defaultColumns.map((column) => column.key))

        if (!isDefaultColumns) {
            query.push({
                key: 'columns',
                value: newState.columns.sort().join(','),
            })
        }

        const queryString = query
            .map((param) => param.key + '=' + param.value)
            .join('&')

        const pathname = queryString ? `?${queryString}` : location.pathname

        navigate(pathname)
    }

    // Watch search params changes
    useEffect(() => {
        // Search available filters in URL
        const filters = {}

        for (const availableFilter of availableFilters) {
            if (searchParams.get(availableFilter.key)) {
                const filterType = availableFilter.type

                if (filterType === 'multiselect') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key).split(',')
                } else if (filterType === 'select') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key)
                } else if (filterType === 'boolean') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key) === 'true'
                }
            }
        }

        // Find query search
        const query = searchParams.get('query') ?? null

        // Find columns
        let columns = []

        // If no columns in URL, use default columns
        if (!searchParams.get('columns')) {
            for (const column of availableColumns) {
                if (column.display === 'optional_default') {
                    columns.push(column.key)
                }
            }
        } else {
            columns = searchParams.get('columns').split(',')
        }

        // Location id
        const location_id = searchParams.get('location_id') ?? null

        // Update state
        const newState = {
            filters,
            query,
            columns,
            location_id,
        }

        setKanbanState(newState)

        // Fetch data
        fetchData(newState)
    }, [searchParams])

    // Toggling columns
    const toggleColumn = (column) => {
        const isInColumns = kanbanState.columns.includes(column.key)

        // If column is already in columns and the last one, don't remove it
        if (isInColumns && kanbanState.columns.length === 1) return

        const newColumns = isInColumns
            ? kanbanState.columns.filter((col) => col !== column.key)
            : [...kanbanState.columns, column.key]

        updateURLWithNewState({
            ...kanbanState,
            columns: newColumns,
        })
    }

    const changeColumns = (columns) => {
        updateURLWithNewState({ ...kanbanState, columns })
    }

    const resetColumns = () => {
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        updateURLWithNewState({
            ...kanbanState,
            columns: defaultColumns,
        })
    }

    // Views
    const selectView = (view) => {
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        updateURLWithNewState({
            ...kanbanState,
            filters: view.filters,
            columns: view?.columns ?? defaultColumns,
            location_id: view?.location_id ?? null,
        })
    }

    const resetView = () => {
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        updateURLWithNewState({
            ...kanbanState,
            filters: {},
            columns: defaultColumns,
        })
    }

    return {
        kanbanState,
        kanbanData,
        isFetching: isFetching || loading,
        refetchData,
        fetchMore,
        fetchingMoreColumns,
        columns,
        changeFilter,
        changeFilters,
        query,
        setQuery,
        resetFilters,
        toggleColumn,
        resetColumns,
        changeColumns,
        selectView,
        resetView,
        changeLocationId,
    }
}
